
export const MIN_ROLES_FOR_ORG_DESIGN = 5;

// POST /create-org-redesign-version (doc §5) rejects with this specific
// error_code when a PUBLISHED version already exists for the PAIC project —
// an expected, recoverable state (there's already something to open), not a
// real failure, so it's handled as its own case rather than shown as a
// generic error.
export const PUBLISHED_VERSION_EXISTS_ERROR_CODE = 7007;

// Shown when a version's PAIC agent run (selection_status) hasn't finished
// yet — the group/version is greyed out and blocked from selection until it
// completes.
export const AGENT_VERSION_NOT_COMPLETED_MESSAGE = "Agent Version is not completed";

// A run that reached selection_status "Failed" did finish — just
// unsuccessfully — so it gets its own message rather than reusing the
// "not completed" copy above, which would misleadingly suggest waiting it
// out will help.
export const AGENT_VERSION_FAILED_MESSAGE = "Agent Version run failed";

// sessionStorage key remembering which PAIC group's Select Version step the
// user was on right before an auto-redirect to the workspace (an already-
// generated version was picked). Navigating away swaps this whole route out
// for /org-design/workspace, so a plain browser Back remounts this wizard
// fresh at Select PAIC Group — this lets it jump straight back to Select
// Version for that same group instead.
export const PAIC_IMPORT_RETURN_GROUP_KEY = "orgDesignPaicImport.returnToProjectId";

// GET /versions (doc §8) rejects with this error_code instead of returning
// an empty array when the org/user has no published versions yet — an
// expected empty state, not a real failure, so it's treated as "no versions"
// rather than shown as a generic error.
export const ORG_REDESIGN_VERSION_NOT_FOUND_ERROR_CODE = 7004;

export const ORG_REDESIGN_SAVE_FAILED_MESSAGE =
  "We couldn't save your edits — they're still in the workspace. Please try Save Version again.";

// Create/update failures carry a readable `message` (e.g. 404 + 7004 "Org
// Redesign version not found", 400 + 7042 for a rejected edit) — shown
// as-is rather than guessed at from the status code, since the same status
// covers several different causes. Generic copy only when there's none.
export const getOrgRedesignSaveErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message.trim() : ORG_REDESIGN_SAVE_FAILED_MESSAGE;
};

// archived_status on an Org Redesign version row — same values PAIC groups use.
export const ORG_REDESIGN_ARCHIVE_STATUS = {
  ARCHIVED: "ARCHIVED",
  ACTIVE: "ACTIVE",
};

export const ORG_REDESIGN_ARCHIVE_FAILED_MESSAGE = "Unable to archive this Organisation Design. Please try again.";

// GET /versions already leaves archived versions out. Filtering again here
// keeps a stale or older response from showing one.
export const excludeArchivedVersions = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((row) => row?.archived_status !== ORG_REDESIGN_ARCHIVE_STATUS.ARCHIVED);

// Backend only lets a version's creator archive it, so the action is only
// offered on rows this user created. This hides the action in the UI; the API
// still enforces ownership.
export const isVersionOwnedBy = (row, sbUserUniqueId) =>
  !!sbUserUniqueId && row?.sb_user_unique_id === sbUserUniqueId;

// A 4xx carries a readable reason (e.g. 403 "You do not own this Org Redesign
// version", 404 "not found") that is worth showing. A 5xx message can carry
// raw exception text, so it always gets the generic copy instead.
export const getOrgRedesignArchiveErrorMessage = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;
  const isClientError = typeof status === "number" && status >= 400 && status < 500;
  return isClientError && typeof message === "string" && message.trim()
    ? message.trim()
    : ORG_REDESIGN_ARCHIVE_FAILED_MESSAGE;
};

// ─── 5-state classification ──────────────────────────────────────────────
// Matches org_redesign_agent.py's finalize() verbatim — not reinvented here.
export const STATUS_LABELS = {
  UNCHANGED: "Unchanged",
  "AI AUGMENTED": "AI Augmented",
  REDUCED: "Reduced",
  REDUNDANT: "Redundant",
  CREATED: "Created",
};

export const STATUS_COLORS = {
  UNCHANGED: "default",
  "AI AUGMENTED": "processing",
  REDUCED: "warning",
  REDUNDANT: "error",
  CREATED: "success",
};

const STATUS_ACCENT_TOKEN = {
  UNCHANGED: "tertiary",
  "AI AUGMENTED": "info",
  REDUCED: "warningBorder",
  REDUNDANT: "danger",
  CREATED: "success",
};

export const ENTRY_OPTION = {
  EXISTING_ORG: "existing_org",
  PAIC_RECOMMENDED: "paic_recommended",
};

// "Augmented" is an AI-recommendation concept (a PAIC agent boosting a
// role's capacity/productivity, possibly with no headcount change at all —
// see [[project_org_design_augmented_status_semantics]]). A hand-built
// "existing org" workspace has no AI behind an AUGMENTED status — a role
// only ever lands there because someone manually raised its target
// headcount — so the same status is relabeled "Increased" for that entry
// path. This is display-only: the underlying status value stays AUGMENTED
// everywhere (stats, filters, colors, the save payload) so nothing about
// how it's computed or persisted changes, only how it reads.
const augmentedDisplayLabel = (entryOption) =>
  entryOption === ENTRY_OPTION.EXISTING_ORG ? "Increased" : STATUS_LABELS["AI AUGMENTED"];

// Other spellings the backend returns for the same verdict — saved versions
// come back as "AUGMENTED" (PAIC agent) or "INCREASED" (Current Org) rather
// than "AI AUGMENTED". A pure rename of the backend's own status, never a
// re-derivation from headcount, so an unrecognised spelling can't fall
// through to an uncolored raw-text tag or drop out of the status counts.
const STATUS_ALIASES = {
  AUGMENTED: "AI AUGMENTED",
  INCREASED: "AI AUGMENTED",
};

export const toCanonicalStatus = (statusRaw) => {
  const status = String(statusRaw || "UNCHANGED").trim().toUpperCase();
  return STATUS_ALIASES[status] || status;
};

// Single source of truth for how a role's status renders anywhere in the Org
// Design UI (tree node accent + tag, table Status cell, legend) — one lookup
// instead of scattering status-keyed if/else across components. `entryOption`
// is optional — omit it (as PAIC contexts do) to always get the AI-facing
// "Augmented" label.
export const getRoleChangeConfig = (statusRaw, entryOption) => {
  const status = toCanonicalStatus(statusRaw);
  const label = status === "AI AUGMENTED" ? augmentedDisplayLabel(entryOption) : STATUS_LABELS[status] || statusRaw;
  return {
    status,
    label,
    color: STATUS_COLORS[status] || "default",
    accentToken: STATUS_ACCENT_TOKEN[status] || STATUS_ACCENT_TOKEN.UNCHANGED,
  };
};

// Sourced from the budget sync response (baseline/added/augmented/reduced/
// redundant_role_count), not the client-computed statusCounts — the
// backend's counts are the source of truth here. A field the response
// doesn't return stays undefined and is filtered out rather than shown as a
// guessed 0. Baseline has no status/color of its own — it's a starting
// count, not a change. Shared by OrgDesignSummaryBridge and the PDF
// export's Summary page.
export const getRoleCountCards = (roleCounts, entryOption) =>
  [
    { key: "baseline", label: "Baseline", count: roleCounts?.baseline },
    { key: "added", label: getRoleChangeConfig("CREATED").label, count: roleCounts?.added, status: "CREATED" },
    { key: "augmented", label: getRoleChangeConfig("AI AUGMENTED", entryOption).label, count: roleCounts?.augmented, status: "AI AUGMENTED" },
    { key: "reduced", label: getRoleChangeConfig("REDUCED").label, count: roleCounts?.reduced, status: "REDUCED" },
    { key: "redundant", label: getRoleChangeConfig("REDUNDANT").label, count: roleCounts?.redundant, status: "REDUNDANT" },
  ].filter((card) => card.count != null);

export const TEXT = {
  PAGE_TITLE: "Organisation Design",
  PAGE_DESCRIPTION:
    "Turn a PAIC run's AI-agent recommendations — or your current organization — into a clear before/after design with headcount changes.",
  OPTION_A_TITLE: "Create workspace within existing org",
  OPTION_A_DESC: "Start from your current org hierarchy and design the target state by hand, with AI Evaluation available as a second opinion.",
  OPTION_B_TITLE: "Start with a PAIC recommended org",
  OPTION_B_DESC: "Import a completed PAIC run's AI-agent impact and generate an AI-recommended before/after org design to edit from.",
  PREVIOUS_ITERATIONS: "Previous Iterations",
  ORG_CONTEXT_TAB: "Organization Additional Strategic Context",
  BACK_TO_ORG_DESIGN: "Back to Organisation Design",
  BTN_SAVE_VERSION: "Save Version",
  BTN_DOWNLOAD_PDF: "Download PDF",
  VIEW_ORG: "Org View",
  VIEW_TABLE: "Table View",
};

export const BASELINE_TAG = {
  existing_org: "Current-org baseline",
  paic_recommended: "PAIC-baseline",
};

// Shared by OrgDesignWorkspace.jsx and PaicRecommendedImport.jsx — antd
// column `filters` options built from whatever distinct values actually
// appear in the current dataset, rather than a hardcoded list. `accessor`
// is optional — defaults to reading `key` straight off the row, pass one
// when the filtered value is derived (e.g. an array's length).
export const getFilters = (rows, key, accessor = (r) => r[key]) =>
  [...new Set(rows.map(accessor).filter((v) => v !== undefined && v !== null && v !== ""))]
    .sort((a, b) => (typeof a === "number" ? a - b : String(a).localeCompare(String(b))))
    .map((value) => ({ text: String(value), value }));

// Spread onto a clickable div/table row so it's also operable via keyboard —
// Enter/Space activates it, matching native button semantics.
export const activatableRowProps = (onActivate) => ({
  role: "button",
  tabIndex: 0,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  },
});

// The `from` label every Org Design workspace analytics event carries. Lives
// here rather than in OrgDesignWorkspace.jsx so OrgTreeCard can share it
// without importing its own parent page back (page -> component -> page).
export const ORG_DESIGN_WORKSPACE_FROM = "org_design_workspace";

// ─── Organisation Design guided tour ──────────────────────────────────────────
export const TOUR_MODULE_KEY = "org-design";

// Cross-page tour phases (namespaced). The landing page opens the most recent
// published design so the workspace can show its steps, then the workspace
// hands back for the landing page's Finish step.
export const CROSS_PAGE_TOUR_PHASE = {
  WORKSPACE: "org-design:workspace", // show the workspace steps there
  FINISH: "org-design:finish", // back on the landing page, resume at Finish
};

// `state`: "always" runs in every branch; "data" only when at least one
// published design exists to open. `entryOption` steps are skipped for users
// who can't see that entry card. `page` steps are shown by the workspace.
export const TOUR_STEPS = [
  {
    // Welcome — centered greeting
    state: "always",
    title: "Welcome to Organisation Design",
    description:
      "This is where you can turn your current organisation or a completed People + AI run into a clear before-and-after design.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    state: "always",
    key: "entryExisting",
    entryOption: ENTRY_OPTION.EXISTING_ORG,
    title: "Start from Your Current Org",
    description: "You can start from your current org hierarchy and design the target state by hand.",
    placement: "bottom",
  },
  {
    state: "always",
    key: "entryPaic",
    entryOption: ENTRY_OPTION.PAIC_RECOMMENDED,
    title: "Start from a PAIC Recommendation",
    description:
      "Or, if you've completed a People + AI run, SkillsBridge can use the AI agents identified in the run to generate a recommended before-and-after org design for you to review and edit.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "versionsTable",
    title: "Previous Iterations",
    description:
      "Every published org design lives here. You can see the project name, version, where it came from—your current org or a People + AI run—along with who created it and when it was last updated. Click any row to reopen the design.",
    placement: "top",
  },
  {
    state: "data",
    key: "viewToggle",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    title: "Table View vs. Org View",
    description:
      "The workspace has two views. Table View gives you a role-by-role breakdown to review and edit, while Org View lets you visualise the org chart and compare your current and proposed structures side by side.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "roleTable",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    view: "table",
    title: "Roles & Proposed Roles",
    description:
      "Start by shaping your proposed organisation in Table View. Update reporting structures, adjust target headcount, and add or remove positions as needed.",
    placement: "top",
  },
  {
    state: "data",
    key: "addRole",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    view: "table",
    title: "Add Role",
    description:
      "Add Role puts a brand-new role into the proposed structure — its title, where it reports, and how many of them you need.",
    placement: "left",
  },
  {
    state: "data",
    key: "trees",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    view: "org",
    title: "Current vs. Proposed",
    description:
      "Once you've made your changes, switch to Org View to visualise the organisation as an org chart. Your Current structure appears on the left and your Proposed structure on the right, so you can see how the organisation changes at a glance.",
    placement: "top",
  },
  {
    state: "data",
    key: "summary",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    title: "Summary",
    description:
      "The summary brings your changes together, showing how many roles fall into each state and how your proposed design changes overall headcount.",
    placement: "top",
  },
  {
    state: "data",
    key: "saveVersion",
    page: CROSS_PAGE_TOUR_PHASE.WORKSPACE,
    title: "Save Version & Download PDF",
    description:
      "Save and export your design.\n\nUse Save Version to capture your design so you can return to it or publish it later. Download PDF lets you export the view you're currently looking at.",
    placement: "bottom",
  },
  {
    // Finish — centered greeting. Primary brings the entry cards into focus.
    state: "always",
    resumePhase: CROSS_PAGE_TOUR_PHASE.FINISH,
    title: "You're all set!",
    description: "Now you're ready to build your target organisation.",
    primaryText: "Start a Design",
    secondaryText: "Done",
  },
];

// Every step for the current branch, in spec order — including the workspace
// leg, so the landing page can tell when advancing should hop pages.
export const getTourSteps = ({ hasVersions, canAccessCurrentOrg, canAccessPaic }) =>
  TOUR_STEPS.filter((step) => {
    if (step.state === "data" && !hasVersions) return false;
    if (step.entryOption === ENTRY_OPTION.EXISTING_ORG && !canAccessCurrentOrg) return false;
    if (step.entryOption === ENTRY_OPTION.PAIC_RECOMMENDED && !canAccessPaic) return false;
    return true;
  });

// The steps the workspace shows on the tour's behalf.
export const WORKSPACE_TOUR_STEPS = TOUR_STEPS.filter(
  (step) => step.page === CROSS_PAGE_TOUR_PHASE.WORKSPACE,
);
